import type { OnlineApplicationChoice } from '../../../../../OnlineApplication/Model';
import type { ParentStudyViewModel } from '../../../Semester/ParentStudyViewModel';
import type { IOnlineApplicationChoicesHandlers } from '../../Context/OnlineStudyPlanContext';
import { GetSubjectsTotalPoints } from '../../Context/OnlineStudyPlanHelpers';
import type { OnlineSemesterItemViewModel } from '../SubjectsBySemester';
import type { OnlineSemesterViewModel } from './Semester';

export const useSemesterHelper = (
    initialSemester: OnlineSemesterViewModel,
    parentStudy: ParentStudyViewModel,
    setItemState: (
        item: OnlineSemesterItemViewModel,
        setter: (prevItemState: OnlineSemesterItemViewModel) => void
    ) => void,
    choicesHandlers: IOnlineApplicationChoicesHandlers
) => {
    const addElectiveItemToBasket = (item: OnlineSemesterItemViewModel) => {
        const choices = getElectiveItemChoices(item);

        choicesHandlers.addChoicesToBasketHandler(choices);

        setItemState(item, currentItem => {
            currentItem.hasOpenedAvailableElectiveSubjects = false;
        });
    };

    const addObligatoryItemToBasket = (item: OnlineSemesterItemViewModel) => {
        const choice = getObligatoryItemChoice(item);
        choicesHandlers.addChoicesToBasketHandler(choice);
    };

    const removeElectiveItemFromBasket = (item: OnlineSemesterItemViewModel) => {
        const choices = getElectiveItemChoices(item);
        choicesHandlers.removeChoicesFromBasketHandler(choices);
    };

    const removeObligatoryItemFromBasket = (item: OnlineSemesterItemViewModel) => {
        const choice = getObligatoryItemChoice(item);
        choicesHandlers.removeChoicesFromBasketHandler(choice);
    };

    const getHasItemUnusedElectivePoints = (item: OnlineSemesterItemViewModel) =>
        item.electivePointsAvailable > 0 &&
        GetSubjectsTotalPoints(item.selectedElectiveSubjects) < item.electivePointsAvailable;

    const getAllChoicesFromSemester = (items: OnlineSemesterItemViewModel[]) => {
        const choices = [] as OnlineApplicationChoice[];
        items.forEach(i => {
            if (i.obligatorySubject) {
                const choice = getObligatoryItemChoice(i);
                choices.push(choice);
            } else {
                const selectedElectiveChoices = getElectiveItemChoices(i);
                selectedElectiveChoices.forEach(ch => choices.push(ch));
            }
        });

        return choices;
    };

    const getElectiveItemChoices = (item: OnlineSemesterItemViewModel): OnlineApplicationChoice[] =>
        item.selectedElectiveSubjects.map(s => ({
            subject: s,
            parentStudy,
            parentItem: {
                semesterIndex: initialSemester.index,
                itemIndex: item.index,
            },
        }));

    const getObligatoryItemChoice = (item: OnlineSemesterItemViewModel) => ({
        parentStudy,
        subject: item.obligatorySubject,
        parentItem: {
            semesterIndex: initialSemester.index,
            itemIndex: item.index,
        },
    });

    return {
        addElectiveItemToBasket,
        addObligatoryItemToBasket,
        removeElectiveItemFromBasket,
        removeObligatoryItemFromBasket,
        getHasItemUnusedElectivePoints,
        getAllChoicesFromSemester,
    };
};
